import React, { useState, useEffect, useRef } from 'react';
import { auth, db, deleteObject, getStorage, storage, collection, getDocs, getDoc, addDoc, doc, updateDoc, deleteDoc, ref, uploadBytes, getDownloadURL, onAuthStateChanged } from './firebaseConfig.js';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from 'react-router-dom';
import "./App.css";
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle.min';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort } from '@fortawesome/free-solid-svg-icons';

const Admin = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editPost, setEditPost] = useState(null);

    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [content1, setContent1] = useState('');
    const [content2, setContent2] = useState('');
    const [content3, setContent3] = useState('');
    const [avatar, setAvatar] = useState('');
    const [coverImage, setCoverImage] = useState('');

    const addPostModalRef = useRef(null);
    const editPostModalRef = useRef(null);

    const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

    const navigate = useNavigate();

    useEffect(() => {
        // Check user authentication
        onAuthStateChanged(auth, (user) => {
            if (!user) {
                navigate('/login');
            } else {
                console.log('User is logged in', user);
            }
        });

        // Fetch posts on load
        getPosts();
    }, [navigate]);

    useEffect(() => {
        if (editPost) {
            setTitle(editPost.title);
            setSubtitle(editPost.subtitle);
            setContent1(editPost.content1);
            setContent2(editPost.content2);
            setContent3(editPost.content3);
            setAvatar(editPost.avatar);
            setCoverImage(editPost.coverImage);
        }
    }, [editPost]);

    // Fetch all posts from Firestore
    const getPosts = async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(collection(db, "posts"));
            const postsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Nếu không tìm kiếm, set tất cả bài viết
            setPosts(postsData);
            return postsData;
        } catch (error) {
            console.error('Error getting posts:', error);
        } finally {
            setLoading(false);
        }
    };

    // Add a new post
    const addPost = async (postData) => {
        setLoading(true);
        try {
            const avatarFile = document.getElementById("avatar").files[0];
            const coverImageFile = document.getElementById("coverImage").files[0];

            let avatarURL = '';
            let coverImageURL = '';

            if (avatarFile) avatarURL = await uploadImage(avatarFile, 'avatars');
            if (coverImageFile) coverImageURL = await uploadImage(coverImageFile, 'coverImages');

            await addDoc(collection(db, "posts"), { ...postData, avatar: avatarURL, coverImage: coverImageURL });
            alert('Post added successfully!');
            getPosts(); // Refresh posts list

            setTitle('');
            setSubtitle('');
            setContent1('');
            setContent2('');
            setContent3('');
            setAvatar('');
            setCoverImage('');

            const modal = bootstrap.Modal.getInstance(addPostModalRef.current);
            modal.hide();
        } catch (error) {
            alert('Error adding post: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Upload image to Firebase Storage
    const uploadImage = async (file, folder) => {
        const storageRef = ref(storage, `${folder}/${file.name}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
    };

    // Edit a post
    const editPostHandler = async (id) => {
        const docRef = doc(db, "posts", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            setEditPost({ id, ...docSnap.data() });
        }
    };

    // Save the edited post
    const saveEditedPost = async (id, postData) => {
        setLoading(true);
        try {
            await updateDoc(doc(db, "posts", id), postData);
            alert('Post updated successfully!');
            setEditPost(null); // Close the edit modal
            setTitle('');
            setSubtitle('');
            setContent1('');
            setContent2('');
            setContent3('');
            setAvatar('');
            setCoverImage('');
            setLoading(false);

            const modal = bootstrap.Modal.getInstance(editPostModalRef.current);
            modal.hide();

            getPosts(); // Refresh posts list
        } catch (error) {
            alert('Error updating post: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Delete a post
    const deletePostHandler = async (id) => {
        if (window.confirm('Are you sure you want to delete this post?')) {
            try {
                setLoading(true);
                // Lấy thông tin bài viết từ Firestore
                const postDoc = await getDoc(doc(db, "posts", id));
                const postData = postDoc.data();
    
                // Lấy đường dẫn ảnh từ bài viết
                const avatar = postData.avatar; 
                const coverImage = postData.coverImage;
    
                // Xóa ảnh từ Firebase Storage
                const storage = getStorage();
                const avatarRef = ref(storage, avatar);
                await deleteObject(avatarRef);

                const coverImageRef = ref(storage, coverImage);
                await deleteObject(coverImageRef);
    
                // Xóa bài viết từ Firestore
                await deleteDoc(doc(db, "posts", id));
                setLoading(false);
                alert('Post and image deleted!');
                getPosts(); // Refresh posts list
            } catch (error) {
                alert('Error deleting post: ' + error.message);
                setLoading(false);
            }
        }
    };

    // Search posts
    const searchPosts = async (searchTerm) => {
        setSearchTerm(searchTerm);
        if (searchTerm === '') {
            getPosts();
            return;
        }

        const allPosts = await getPosts();
        const filteredPosts = allPosts.filter(post =>
            post.title.toLowerCase().includes(searchTerm.toLowerCase())
        );

        setPosts(filteredPosts);
    }

    const sortedPosts = React.useMemo(() => {
        let sortablePosts = [...posts];
        if (sortConfig.key) {
            sortablePosts.sort((a, b) => {
                const aValue = a[sortConfig.key].toString().toLowerCase();
                const bValue = b[sortConfig.key].toString().toLowerCase();
    
                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortablePosts;
    }, [posts, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    return (
        <div className="container py-5">
            <h1 className="text-center">Admin Page</h1>

            {/* Loading indicator */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-indicator"></div>
                </div>
            )}

            {/* Search */}
            <div className="form-group d-flex">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Search posts"
                    value={searchTerm}
                    onChange={(e) => searchPosts(e.target.value)}
                />
            </div>

            {/* Add post button */}
            <button
                className="btn btn-primary mt-3"
                data-bs-toggle="modal"
                data-bs-target="#addPostModal"
            >
                Add Post
            </button>

            {/* Posts list */}
            <h2 className='text-center'>Post List</h2>
            <table className="table table-bordered">
                <thead>
                    <tr>
                        <th onClick={() => requestSort('title')}>
                            <div className="d-flex justify-content-between align-items-center">
                                <span>Title</span>
                                <FontAwesomeIcon icon={faSort} />
                            </div>
                        </th>
                        <th onClick={() => requestSort('subtitle')}>
                            <div className="d-flex justify-content-between align-items-center">
                                <span>Subtitle</span>
                                <FontAwesomeIcon icon={faSort} />
                            </div>
                        </th>
                        <th onClick={() => requestSort('date')}>
                            <div className="d-flex justify-content-between align-items-center">
                                <span>Published Date</span>
                                <FontAwesomeIcon icon={faSort} />
                            </div>
                        </th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedPosts.map(post => (
                        <tr key={post.id}>
                            <td>{post.title}</td>
                            <td>{post.subtitle}</td>
                            <td>{new Date(post.date.seconds * 1000).toLocaleDateString()}</td>
                            <td className='d-flex gap-2'>
                                <button className="btn btn-warning" onClick={() => editPostHandler(post.id)} data-bs-toggle="modal" data-bs-target="#editPostModal">
                                    Edit
                                </button>
                                <button className="btn btn-danger" onClick={() => deletePostHandler(post.id)}>Delete</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Add Post Modal */}
            <div className="modal fade" id="addPostModal" tabIndex="-1" aria-labelledby="addPostModalLabel" aria-hidden="true" ref={addPostModalRef}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="addPostModalLabel">Add Post</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => {
                                setTitle('');
                                setSubtitle('');
                                setContent1('');
                                setContent2('');
                                setContent3('');
                                setAvatar('');
                                setCoverImage('');
                            }}></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={(e) => { e.preventDefault(); addPost({ title: e.target.title.value, subtitle: e.target.subtitle.value, content1: e.target.content1.value, content2: e.target.content2.value, content3: e.target.content3.value, date: new Date() }) }}>
                                <div className="mb-3">
                                    <label htmlFor="title" className="form-label">Title</label>
                                    <input type="text" id="title" value={title} onChange={(e) => setTitle(e.target.value)} className="form-control" required />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="subtitle" className="form-label">Subtitle</label>
                                    <input type="text" id="subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className="form-control" required />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="content1" className="form-label">Content 1</label>
                                    <textarea id="content1" value={content1} onChange={(e) => setContent1(e.target.value)} className="form-control" required></textarea>
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="content2" className="form-label">Content 2</label>
                                    <textarea id="content2" value={content2} onChange={(e) => setContent2(e.target.value)} className="form-control" required></textarea>
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="content3" className="form-label">Content 3</label>
                                    <textarea id="content3" value={content3} onChange={(e) => setContent3(e.target.value)} className="form-control" required></textarea>
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="avatar" className="form-label">Avatar Image</label>
                                    <input type="file" id="avatar" onChange={(e) => setAvatar(e.target.files[0])} className="form-control" />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="coverImage" className="form-label">Cover Image</label>
                                    <input type="file" id="coverImage" onChange={(e) => setCoverImage(e.target.files[0])} className="form-control" />
                                </div>
                                <button type="submit" className="btn btn-primary">Add Post</button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Post Modal */}
            <div className="modal fade" id="editPostModal" tabIndex="-1" aria-labelledby="editPostModalLabel" aria-hidden="true" ref={editPostModalRef}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="editPostModalLabel">Edit Post</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => {
                                setEditPost(null);
                                setTitle('');
                                setSubtitle('');
                                setContent1('');
                                setContent2('');
                                setContent3('');
                                setAvatar('');
                                setCoverImage('');
                            }}></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={(e) => { e.preventDefault(); saveEditedPost(editPost.id, { title: e.target.title.value, subtitle: e.target.subtitle.value, content1: e.target.content1.value, content2: e.target.content2.value, content3: e.target.content3.value }) }}>
                                <div className="mb-3">
                                    <label htmlFor="title" className="form-label">Title</label>
                                    <input type="text" id="title" value={editPost ? title : ""} onChange={(e) => setTitle(e.target.value)} className="form-control" required />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="subtitle" className="form-label">Subtitle</label>
                                    <input type="text" id="subtitle" value={editPost ? subtitle : ""} onChange={(e) => setSubtitle(e.target.value)} className="form-control" required />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="content1" className="form-label">Content 1</label>
                                    <textarea id="content1" value={editPost ? content1 : ""} onChange={(e) => setContent1(e.target.value)} className="form-control" required></textarea>
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="content2" className="form-label">Content 2</label>
                                    <textarea id="content2" value={editPost ? content2 : ""} onChange={(e) => setContent2(e.target.value)} className="form-control" required></textarea>
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="content3" className="form-label">Content 3</label>
                                    <textarea id="content3" value={editPost ? content3 : ""} onChange={(e) => setContent3(e.target.value)} className="form-control" required></textarea>
                                </div>
                                {/* preview avatar */}
                                <div className="mb-3 ">
                                    <label htmlFor="avatar" className="form-label">Avatar Image</label>
                                    <div className="d-flex align-items-center gap-3">
                                        <img src={editPost ? avatar : ""} alt="avatar" width="100" height="100" />
                                        <input
                                            type="file"
                                            id="avatar"
                                            onChange={(e) => setAvatar(e.target.files[0])}
                                            className="form-control"
                                        />

                                    </div>
                                </div>

                                {/* preview coverImage */}
                                <div className="mb-3 ">
                                    <label htmlFor="coverImage" className="form-label">Cover Image</label>
                                    <div className="d-flex align-items-center gap-3">
                                        <img src={editPost ? coverImage : ""} alt="coverImage" width="100" height="100" />
                                        <input
                                            type="file"
                                            id="coverImage"
                                            onChange={(e) => setCoverImage(e.target.files[0])}
                                            className="form-control"
                                        />

                                    </div>
                                </div>

                                <button type="submit" className="btn btn-primary">Save Changes</button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

        </div >
    );
};

export default Admin;
